import { getIntl } from 'umi';

import { selectCommercialBillingApiRoute } from '@/components/SiderMenu/utils/sessionNavigation';
import { initialPagination, initialState, makeListReducers } from '@/models/utils';
import { bindServices } from '@/services/api';
import CoreQueries from '@/utils/input/queries';

import {
  DEFAULT_ORDER_BY,
  FALLBACK_API_ROUTE,
  MODEL_NAME,
  NAVIGATION_KEY,
  REGISTRATION_REVIEW_ACTION,
} from './constants';
import {
  applyDefaultPendingStatusFilter,
  filtersConfig,
  isPendingRegistrationApproval,
} from './utils';

const api = bindServices(MODEL_NAME);
const pagedQuery = CoreQueries.ChannelRegistrationApproval.paged;

function* resolveApiRoute(select) {
  return (yield* selectCommercialBillingApiRoute(NAVIGATION_KEY, select)) || FALLBACK_API_ROUTE;
}

const getHttpStatus = (error) => error?.status || error?.response?.status || error?.data?.status;

/** Pattern X — read-only queue with Automy approve/reject. */
const moduleInitialState = {
  ...initialState,
  list: [],
  orderBy: DEFAULT_ORDER_BY,
  select: pagedQuery.select,
  searchSelect: pagedQuery.searchSelect,
  showForm: false,
  detail: false,
  id: null,
  item: null,
  reviewModal: { state: false, id: null, action: null },
  success: false,
  activatingItemIds: {},
  filtersConfig,
};

const ChannelRegistrationApprovalsModel = {
  namespace: MODEL_NAME,
  state: moduleInitialState,

  effects: {
    *fetchList({ payload }, { call, put, select }) {
      try {
        const fetchRoute = yield* resolveApiRoute(select);
        if (!fetchRoute) return;

        const pagination = yield select((state) => state[MODEL_NAME].pagination);
        const pageSize = yield select((state) => state.global.pageSize);
        const params = applyDefaultPendingStatusFilter({
          limit: pageSize,
          ...pagedQuery,
          ...pagination,
          ...payload,
        });
        const res = yield call(api.getAll, {
          route: `${fetchRoute}/paged`,
          params,
          readPriority: true,
        });

        const items = res?.items || res?.data || (Array.isArray(res) ? res : []);
        yield put({
          type: 'queryData',
          payload: {
            ...res,
            items,
            limit: pageSize,
            page: payload.page,
            totalCount: res?.totalCount ?? res?.count ?? res?.total ?? items.length,
          },
        });
      } catch (e) {
        /*  */
      }
    },

    *fetchItem({ payload }, { call, put, select }) {
      try {
        const fetchRoute = yield* resolveApiRoute(select);
        if (!fetchRoute) return;

        yield put({ type: 'success', payload: { success: false } });
        const res = yield call(api.getById, {
          route: fetchRoute,
          id: payload.id,
          params: {
            ...(payload.detail ? CoreQueries.ChannelRegistrationApproval?.itemDetail : {}),
            ...(payload?.params && { ...payload.params }),
          },
        });
        const item = Array.isArray(res) ? res[0] : res;
        yield put({ type: 'queryItem', payload: item });
      } catch (e) {
        /*  */
      }
    },

    *showReviewModal({ payload }, { put }) {
      yield put({ type: 'queryShowReviewModal', payload });
    },

    *closeReviewModal(_, { put }) {
      yield put({ type: 'queryShowReviewModal', payload: { state: false, id: null } });
    },

    *reviewRequest({ payload }, { call, put, select }) {
      const { id, action, reviewNotes, rejectionReason } = payload || {};
      const isApprove = action === REGISTRATION_REVIEW_ACTION.APPROVE;
      const isReject = action === REGISTRATION_REVIEW_ACTION.REJECT;
      if (!id || (!isApprove && !isReject)) return;

      const reason = String(rejectionReason || '').trim();
      if (isReject && !reason) return;

      yield put({ type: 'success', payload: { success: false } });

      try {
        const fetchRoute = yield* resolveApiRoute(select);
        if (!fetchRoute) return;

        const freshRes = yield call(api.getById, {
          route: fetchRoute,
          id,
        });
        const fresh = Array.isArray(freshRes) ? freshRes[0] : freshRes;
        const expectedRowVersion = fresh?.rowVersion;
        const currentId = yield select((state) => state[MODEL_NAME].id);
        if (currentId === id) {
          yield put({ type: 'queryItem', payload: fresh });
        }

        if (!isPendingRegistrationApproval(fresh)) {
          yield put({ type: 'queryShowReviewModal', payload: { state: false, id: null } });
          yield put({ type: 'success', payload: { success: true } });
          return;
        }

        if (!expectedRowVersion) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: {
              visible: true,
              type: 'error',
              message: getIntl().formatMessage({ id: 'channelRegistrationApprovals.conflict' }),
            },
          });
          return;
        }

        const notes = String(reviewNotes || '').trim();
        const data = { expectedRowVersion };
        if (notes) {
          data.reviewNotes = notes;
        }
        if (isReject) {
          data.rejectionReason = reason;
        }

        const reviewRes = yield call(api.apiRoute, {
          route: `${fetchRoute}/${id}/${action}`,
          method: 'post',
          data,
        });

        const reviewed = Array.isArray(reviewRes) ? reviewRes[0] : reviewRes;
        yield put({ type: 'queryShowReviewModal', payload: { state: false, id: null } });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        if (currentId === id) {
          if (reviewed?.id) {
            yield put({ type: 'queryItem', payload: reviewed });
          } else {
            yield put({ type: 'fetchItem', payload: { id, detail: true } });
          }
        }
      } catch (e) {
        const status = getHttpStatus(e);
        if (status === 409) {
          yield put({ type: 'queryShowReviewModal', payload: { state: false, id: null } });
          yield put({ type: 'fetchList', payload: { page: 1 } });
          const currentId = yield select((state) => state[MODEL_NAME].id);
          if (currentId === id) {
            yield put({ type: 'fetchItem', payload: { id, detail: true } });
          }
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: {
              visible: true,
              type: 'error',
              message: getIntl().formatMessage({ id: 'channelRegistrationApprovals.conflict' }),
            },
          });
          return;
        }
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      }
    },

    *setOrderBy({ payload }, { put }) {
      yield put({
        type: 'queryOrderBy',
        payload,
      });
    },

    *showForm({ payload }, { put }) {
      yield put({
        type: 'queryShowForm',
        payload,
      });
    },

    *changeSuccess({ payload }, { put }) {
      yield put({ type: 'success', payload });
    },

    *resetState(_, { put }) {
      yield put({ type: 'reset' });
    },
  },

  reducers: {
    ...makeListReducers({
      initialPagination,
      initialState: moduleInitialState,
    }),
    queryShowReviewModal(state, { payload }) {
      return {
        ...state,
        reviewModal: {
          state: !!payload?.state,
          id: payload?.id ?? null,
          action: payload?.action ?? null,
        },
      };
    },
    setFiltersConfig(state, { payload }) {
      return {
        ...state,
        filtersConfig: payload,
      };
    },
  },
};

export default ChannelRegistrationApprovalsModel;
